/** /settings/connectors/outlook-bridge - dedicated Configure flow
 *  for the Outlook Bridge (a VSTO add-in running inside Classic
 *  Outlook on a machine the user controls). The bridge URL is a host
 *  the user owns: a LAN IP, a hostname, an FQDN, or a Tailscale
 *  MagicDNS name. There is no auto-detect (a URL you own is not
 *  discoverable by scanning). Instead the page teaches the user where
 *  the installer prints the URL, validates the format only, and Test
 *  Connection confirms the live build.sha. No em dashes. */
import { useCallback, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check,
  X as XIcon,
  Loader2,
  Plug,
  Info,
  ExternalLink,
  HelpCircle,
} from "lucide-react";
import { ScopeOption } from "../components/ScopeOption";
import { TeachingBanner } from "../components/TeachingBanner";
import { getCsrfToken } from "../lib/auth";

const BRIDGE_REPO = "https://github.com/wwahmed/waki-outlook-bridge";
// No hardcoded default. The bridge URL is a host the user owns and
// the Parallels IP is not stable across VM rebuilds, so the field
// starts blank and the placeholder just shows the shape of a valid
// value (a LAN IP or a Tailscale MagicDNS name).
const BRIDGE_URL_PLACEHOLDER =
  "e.g. http://10.211.55.5:8191 or http://wakibridge.tail-xxx.ts.net:8191";

const PERSONAL_SUBS = [
  "general",
  "family",
  "social",
  "medical",
  "financial",
  "business",
] as const;

function csrfHeaders(): Record<string, string> {
  const t = getCsrfToken();
  return {
    "Content-Type": "application/json",
    ...(t ? { "X-CSRF-Token": t } : {}),
  };
}

/** Format-only validation. Accepts IPv4, IPv6, hostname, FQDN, and
 *  Tailscale MagicDNS names: anything that parses as an http(s) URL
 *  with a host. We do NOT restrict the host contents (that is the
 *  user's network, not ours). Empty is treated as "not yet entered",
 *  not invalid, so the field does not shout before the user types. */
function bridgeUrlError(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return "Enter a full URL including http:// or https:// and the port.";
  }
  if (u.protocol !== "http:" && u.protocol !== "https:")
    return "URL must start with http:// or https://.";
  if (!u.hostname) return "URL is missing a host.";
  return null;
}

/** Shared first-time setup steps, used both in the top panel and the
 *  inline "Where do I find this?" expander so the teaching copy lives
 *  in exactly one place. */
function FirstTimeSetup() {
  return (
    <ol className="list-decimal pl-5 space-y-1.5">
      <li>
        Install Outlook on the machine that will host the bridge (any
        Mac or PC reachable from this device).
      </li>
      <li>
        Clone or download{" "}
        <code className="rounded bg-sky-100 dark:bg-sky-900/40 px-1.5 py-0.5">
          waki-outlook-bridge
        </code>{" "}
        and run{" "}
        <code className="rounded bg-sky-100 dark:bg-sky-900/40 px-1.5 py-0.5">
          scripts/install-bridge.ps1 -Restart
        </code>{" "}
        in an elevated PowerShell.
      </li>
      <li>
        The installer prints "Bridge is reachable at:" followed by a
        list of URLs. Pick one and paste it here.
      </li>
      <li>
        Or run{" "}
        <code className="rounded bg-sky-100 dark:bg-sky-900/40 px-1.5 py-0.5">
          curl http://&lt;any-known-bridge-host&gt;:8191/whereami
        </code>{" "}
        from any machine on the same network. It returns the same list
        as JSON.
      </li>
      <li>
        Tailscale users: install Tailscale on the bridge machine for a
        stable URL that works across networks and survives IP changes.
      </li>
    </ol>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

interface TestResult {
  ok: boolean;
  healthz: boolean;
  gal_resolve: boolean;
  folders: boolean;
  version: string | null;
  build_sha: string | null;
  folder_count: number | null;
  error: string | null;
  suggested_action: string | null;
}

export function OutlookBridgeConfigure() {
  const nav = useNavigate();
  const [showHelp, setShowHelp] = useState(true);
  const [showFindHelp, setShowFindHelp] = useState(false);
  const [label, setLabel] = useState("");
  const [bridgeUrl, setBridgeUrl] = useState("");
  const [hmac, setHmac] = useState("");
  const [hmacGenerated, setHmacGenerated] = useState(false);
  const [primaryScope, setPrimaryScope] = useState<"" | "personal" | "work">(
    "",
  );
  const [sub, setSub] = useState("");
  const [syncCalendar, setSyncCalendar] = useState(true);
  const [syncEmail, setSyncEmail] = useState(true);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<TestResult | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const urlErr = bridgeUrlError(bridgeUrl);
  const canTest = !!bridgeUrl.trim() && !urlErr && !testing;

  const runTest = useCallback(async () => {
    setTesting(true);
    setErr(null);
    setTest(null);
    try {
      const res = await fetch("/api/work-connectors/outlook-bridge/test", {
        method: "POST",
        headers: csrfHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ bridge_url: bridgeUrl.trim(), hmac_secret: hmac }),
      });
      const data = (await res.json()) as Record<string, unknown>;
      if (!res.ok && !("healthz" in data))
        throw new Error(
          (data.error as string) || `probe failed (${res.status})`,
        );
      setTest(data as unknown as TestResult);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setTesting(false);
    }
  }, [bridgeUrl, hmac]);

  const canSave =
    !!label.trim() && !!primaryScope && !urlErr && !!test?.ok && !saving;

  const save = useCallback(async () => {
    if (!canSave) return;
    setSaving(true);
    setErr(null);
    try {
      const res = await fetch("/api/connectors", {
        method: "POST",
        headers: csrfHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({
          // Internal kind renamed from the leaky "mail-viewer" to
          // "outlook-bridge" so the connector card reads as "Outlook
          // Bridge" (the kind.replace(/[-_]/g, ' ') + CSS capitalize
          // renders the kind string as the card name).
          kind: "outlook-bridge",
          account: label.trim(),
          scope: primaryScope,
          sub_scope:
            primaryScope === "work"
              ? `work:${sub || "opentext"}`
              : `personal:${sub || "general"}`,
          config: {
            // account_identifier keys the per-user UNIQUE index on
            // connectors. The bridge URL is shared infrastructure that
            // one user can legitimately point multiple cards at (Mail
            // / Calendar / etc.), so we use the user-visible label as
            // the disambiguator instead.
            account_identifier: label.trim(),
            // The worker reads the URL from config.base_url. We also
            // mirror it to bridge_url for any older code still keying
            // off that name (and so the Configure modal's Test
            // Connection button can prefill).
            base_url: bridgeUrl.trim(),
            bridge_url: bridgeUrl.trim(),
            sync_calendar: syncCalendar,
            sync_email: syncEmail,
          },
        }),
      });
      // Defensive parse: a backend regression that lets an
      // exception escape Express's default handler returns text/html
      // ("<!DOCTYPE..."). JSON.parse on that crashes the form with
      // an unreadable SyntaxError. Surface a stable error message
      // instead so the form stays usable.
      const ctype = res.headers.get("content-type") || "";
      if (!ctype.toLowerCase().includes("application/json")) {
        const sample = (await res.text().catch(() => "")).slice(0, 120);
        throw new Error(
          `Server returned an unexpected response. Status: ${res.status}.${sample ? ` ${sample}` : ""}`,
        );
      }
      let data: { message?: string; error?: string; existing_id?: string | null } = {};
      try {
        data = await res.json();
      } catch {
        throw new Error(
          `Server returned an unparseable response. Status: ${res.status}.`,
        );
      }
      if (!res.ok) {
        if (res.status === 409 && data.error === "duplicate_connector") {
          throw new Error(
            data.message ||
              "A bridge with this label is already configured for this user. Use a different label.",
          );
        }
        throw new Error(
          data.message || data.error || `could not configure (${res.status})`,
        );
      }
      nav("/settings/connectors");
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }, [canSave, label, primaryScope, sub, bridgeUrl, syncCalendar, syncEmail, nav]);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      <div className="flex items-center gap-2">
        <Plug className="w-5 h-5 text-sky-500" />
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
          Configure Outlook Bridge
        </h1>
      </div>

      <TeachingBanner id="outlook_bridge_configure_overview" />

      {showHelp && (
        <div className="rounded-xl border border-sky-300/60 dark:border-sky-700/50 bg-sky-50/70 dark:bg-sky-950/20 p-4 text-sm text-sky-900 dark:text-sky-100">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="flex-1 space-y-2">
              <p className="font-semibold">
                Setting up the bridge for the first time
              </p>
              <p>
                Outlook Bridge runs as a VSTO add-in inside Classic
                Outlook. Memso connects to it over HTTP, so it can live
                on the same machine as Memso or on a separate host or
                VM. You give Memso the URL; nothing is auto-detected.
              </p>
              <FirstTimeSetup />
              <a
                href={BRIDGE_REPO}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 underline"
              >
                Bridge README <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <button
              type="button"
              onClick={() => setShowHelp(false)}
              className="text-sky-500 shrink-0"
              aria-label="Dismiss"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <Row label="Account label (required)">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="OpenText Work Outlook"
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-3 py-2 text-sm"
          />
        </Row>
        <Row label="Bridge URL (required)">
          <input
            value={bridgeUrl}
            onChange={(e) => setBridgeUrl(e.target.value)}
            placeholder={BRIDGE_URL_PLACEHOLDER}
            spellCheck={false}
            autoCapitalize="none"
            autoCorrect="off"
            className={`w-full rounded-lg border bg-transparent px-3 py-2 text-sm font-mono ${
              urlErr
                ? "border-rose-400 dark:border-rose-500"
                : "border-slate-300 dark:border-slate-600"
            }`}
          />
          {urlErr && (
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
              {urlErr}
            </p>
          )}
          <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
            Where does your bridge live? Run the WakiOutlookBridge
            installer on the machine where Outlook runs. The installer
            prints the URLs your bridge is reachable at. Paste any one
            of them above. If you have Tailscale, prefer the Tailscale
            MagicDNS name. It survives network and IP changes.{" "}
            <button
              type="button"
              onClick={() => setShowFindHelp((v) => !v)}
              className="inline-flex items-center gap-1 text-sky-600 dark:text-sky-400 hover:underline"
            >
              <HelpCircle className="w-3.5 h-3.5" />
              Where do I find this?
            </button>
          </p>
          {showFindHelp && (
            <div className="mt-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/40 p-3 text-xs text-slate-700 dark:text-slate-300 space-y-2">
              <p className="font-semibold">
                Setting up the bridge for the first time:
              </p>
              <FirstTimeSetup />
            </div>
          )}
        </Row>
        <Row label="HMAC secret">
          <div className="space-y-2">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Optional. Leave blank to use the secret configured on the
              Memso server. If your bridge requires its own
              authentication, paste or generate one that matches the
              bridge's config.json.
            </p>
            <div className="flex gap-2">
              <input
                type="password"
                value={hmac}
                onChange={(e) => {
                  setHmac(e.target.value);
                  setHmacGenerated(false);
                }}
                placeholder="Paste or generate the bridge HMAC secret"
                className="flex-1 rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-3 py-2 text-sm font-mono"
              />
              <button
                type="button"
                onClick={() => {
                  const bytes = crypto.getRandomValues(new Uint8Array(32));
                  setHmac(
                    btoa(String.fromCharCode(...bytes)).replace(/=+$/, ""),
                  );
                  setHmacGenerated(true);
                }}
                className="rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm"
              >
                Generate
              </button>
            </div>
            {hmacGenerated && (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Copy this and paste it into your bridge config under
                hmac_secret, then restart Outlook.
              </p>
            )}
            {!hmacGenerated && hmac.length > 0 && hmac.length < 16 && (
              <p className="text-xs text-rose-600 dark:text-rose-400">
                Too short. Use at least 16 characters.
              </p>
            )}
          </div>
        </Row>
        <Row label="Primary scope (required)">
          <div
            role="radiogroup"
            aria-label="Primary scope"
            className="flex gap-2"
          >
            {(["personal", "work"] as const).map((s) => (
              <ScopeOption
                key={s}
                compact
                selected={primaryScope === s}
                onSelect={() => {
                  setPrimaryScope(s);
                  setSub(s === "work" ? "opentext" : "general");
                }}
                label={s === "work" ? "Work" : "Personal"}
              />
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Outlook Bridge is typically Work but you can configure
            otherwise.
          </p>
        </Row>
        {primaryScope && (
          <Row label="Sub-category">
            <select
              value={sub}
              onChange={(e) => setSub(e.target.value)}
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-3 py-2 text-sm capitalize"
            >
              {primaryScope === "work" ? (
                <option value="opentext">OpenText</option>
              ) : (
                PERSONAL_SUBS.map((x) => (
                  <option key={x} value={x}>
                    {x.charAt(0).toUpperCase() + x.slice(1)}
                  </option>
                ))
              )}
            </select>
          </Row>
        )}
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={syncCalendar}
              onChange={(e) => setSyncCalendar(e.target.checked)}
              className="accent-sky-500"
            />
            Sync calendar
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={syncEmail}
              onChange={(e) => setSyncEmail(e.target.checked)}
              className="accent-sky-500"
            />
            Sync email
          </label>
        </div>

        <div>
          <button
            type="button"
            onClick={runTest}
            disabled={!canTest}
            className="inline-flex items-center gap-2 rounded-lg border border-sky-400 text-sky-700 dark:text-sky-300 px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {testing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plug className="w-4 h-4" />
            )}
            Test Connection
          </button>
          {test && (
            <div
              className={`mt-3 rounded-lg border p-3 text-sm ${
                test.ok
                  ? "border-emerald-300 bg-emerald-50/70 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-200"
                  : "border-rose-300 bg-rose-50/70 dark:bg-rose-950/20 text-rose-800 dark:text-rose-200"
              }`}
            >
              {test.ok ? (
                <>
                  <p className="font-semibold flex items-center gap-1.5">
                    <Check className="w-4 h-4" /> Bridge reachable.
                    {test.folder_count != null
                      ? ` ${test.folder_count} folders found.`
                      : ""}
                  </p>
                  <p className="mt-1 text-xs">
                    Bridge build:{" "}
                    <code className="rounded bg-emerald-100 dark:bg-emerald-900/40 px-1.5 py-0.5 font-mono">
                      {test.build_sha || test.version || "unknown"}
                    </code>{" "}
                    Confirm this matches the build you deployed.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold flex items-center gap-1.5">
                    <XIcon className="w-4 h-4" /> Connection failed.
                  </p>
                  <ul className="mt-1 text-xs space-y-0.5">
                    <li>healthz: {test.healthz ? "ok" : "fail"}</li>
                    <li>gal/resolve: {test.gal_resolve ? "ok" : "fail"}</li>
                    <li>folders: {test.folders ? "ok" : "fail"}</li>
                    {test.error ? <li>error: {test.error}</li> : null}
                  </ul>
                  {test.suggested_action && (
                    <p className="mt-2 text-xs">{test.suggested_action}</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {err && (
          <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>
        )}

        <div className="flex items-center gap-3 border-t border-slate-100 dark:border-slate-800 pt-4">
          <button
            type="button"
            onClick={save}
            disabled={!canSave}
            className="rounded-lg bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-40"
          >
            {saving ? "Configuring..." : "Configure"}
          </button>
          <button
            type="button"
            onClick={() => nav("/settings/connectors")}
            className="text-sm text-slate-500 hover:underline"
          >
            Cancel
          </button>
          {!canSave && (
            <span className="text-xs text-slate-400">
              Fill the label, paste a valid Bridge URL, pick a scope,
              and pass Test Connection to enable Configure.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default OutlookBridgeConfigure;
